import React, { FunctionComponent, useMemo, useCallback, useState } from 'react'
import { UploadFilesArea, KennoFile } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { useUploadFile } from 'hooks/useUploadFile'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'

export interface Props {
  className?: string
  id?: string
  files: KennoFile[]
  onUploadStart?: () => void
  onFilesUploaded: (newFiles: KennoFile[]) => void
  onDeleteFile: (fileToRemove: KennoFile) => void
  onUploadEnd?: () => void
  onUploadOngoing?: (uploadOngoing: boolean) => void
  accept?: string
  disabled?: boolean
  single?: boolean
}

export const UploadFiles: FunctionComponent<Props> = ({
  className,
  files,
  onFilesUploaded,
  onDeleteFile,
  onUploadStart,
  onUploadEnd,
  onUploadOngoing,
  accept = '.jpg, .jpeg, .png, .gif, .pdf',
  id,
  disabled = false,
  single,
}) => {
  const { t } = useTranslation('common')
  const { currentContractId } = useCurrentContract()
  const uploadFile = useUploadFile(currentContractId)
  const [uploadOngoing, setUploadOngoing] = useState<boolean>(false)

  const uploadFn = useCallback(
    async (filesToUpload: KennoFile[]): Promise<void> => {
      try {
        setUploadOngoing(true)
        if (onUploadOngoing) onUploadOngoing(true)
        if (onUploadStart) onUploadStart()

        const uploadedFiles = await Promise.all(filesToUpload.map(uploadFile))

        onFilesUploaded(
          uploadedFiles
            .filter((file): file is KennoFile => file !== null)
            .map((file) => ({
              ...file,
              _state: 1,
            }))
        )

        if (onUploadEnd) onUploadEnd()
        if (onUploadOngoing) onUploadOngoing(false)
        setUploadOngoing(false)
      } catch (e) {
        // FIXME: handle errors
        console.log(e) // eslint-disable-line
      }
    },
    [onFilesUploaded, onUploadEnd, onUploadStart, uploadFile, onUploadOngoing]
  )

  const uploadedFiles = useMemo(
    () => files.map((file) => ({ ...file, _state: 1 })),
    [files]
  )

  const onRemove = (fileToRemove: KennoFile): void => {
    if (fileToRemove._state === 1) {
      onDeleteFile(fileToRemove)
    } else if (fileToRemove._abortController)
      fileToRemove._abortController.abort()
  }

  return (
    <UploadFilesArea
      id={id}
      className={className}
      uploadedFiles={uploadedFiles}
      uploadFn={uploadFn}
      onRemove={onRemove}
      accept={accept}
      disabled={disabled || uploadOngoing}
      single={single}
      inputText={t('uploadFilesInputText')}
      errorText={`${t('uploadFilesInputError')} ${accept}.`}
    />
  )
}
